import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { buildLeadMetadata } from "@/lib/utm";
import { toast } from "sonner";
import { Sparkles, ArrowRight, Check } from "lucide-react";

const PLAN_OPTIONS = [
  { id: "starter", name: "Starter", price: 149, tagline: "Organize seu estoque" },
  { id: "growth", name: "Growth", price: 397, tagline: "Preveja demanda com IA", popular: true },
  { id: "pro", name: "Pro", price: 1497, tagline: "Inteligência completa para redes" },
];

export default function Signup() {
  const { signup, loading } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialPlan = (params.get("plan") || "growth").toLowerCase();
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    company: "",
    cpf_cnpj: "",
    phone: "",
    plan: PLAN_OPTIONS.find((p) => p.id === initialPlan) ? initialPlan : "growth",
  });

  const submit = async (e) => {
    e.preventDefault();
    const cpfDigits = (form.cpf_cnpj || "").replace(/\D/g, "");
    if (cpfDigits.length !== 11 && cpfDigits.length !== 14) {
      toast.error("Informe um CPF (11 dígitos) ou CNPJ (14 dígitos) válido");
      return;
    }
    try {
      await signup({
        ...form,
        cpf_cnpj: cpfDigits,
        lead_metadata: buildLeadMetadata({ source: "signup", trial_plan: form.plan }),
      });
      toast.success("Conta criada! 14 dias de trial liberados.");
      navigate("/app/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao criar conta");
    }
  };

  const selectedPlan = PLAN_OPTIONS.find((p) => p.id === form.plan);

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex relative items-center justify-center p-12 overflow-hidden border-r border-border">
        <div className="absolute inset-0 grid-bg opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-tr from-primary/10 via-transparent to-transparent" />
        <div className="relative max-w-md fade-up">
          <Link to="/" className="flex items-center gap-2 mb-10">
            <div className="w-8 h-8 rounded-md bg-primary flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <span className="font-display font-bold text-xl">SmartStockAI</span>
          </Link>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-primary/40 bg-primary/10 text-primary text-xs uppercase tracking-wider mb-5">
            <Sparkles className="w-3 h-3" /> 14 dias grátis · sem cartão
          </div>
          <h1 className="font-display text-5xl font-bold leading-tight tracking-tight">
            Comece em 30 segundos.
          </h1>
          <p className="text-muted-foreground mt-6 leading-relaxed">
            Acesso completo ao plano <strong className="text-foreground">{selectedPlan?.name}</strong> por
            14 dias. Depois, escolha continuar ou cancelar — sem cartão antecipado.
          </p>
          <ul className="space-y-3 mt-10 text-sm">
            {[
              "30 produtos demo + 90 dias de histórico",
              "Acesso completo durante o trial (todas as funcionalidades)",
              "Copiloto IA + previsão + multi-loja",
              "Pagamento PIX ou cartão (Asaas) ao escolher plano",
            ].map((t) => (
              <li key={t} className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full bg-primary/20 border border-primary/30 flex items-center justify-center mt-0.5">
                  <Check className="w-3 h-3 text-primary" />
                </div>
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 overflow-y-auto">
        <form data-testid="signup-form" onSubmit={submit} className="w-full max-w-md space-y-5 fade-up py-8">
          <div>
            <Link to="/" className="flex items-center gap-2 lg:hidden mb-6">
              <div className="w-7 h-7 rounded-md bg-primary flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <span className="font-display font-bold text-lg">SmartStockAI</span>
            </Link>
            <h2 className="font-display text-3xl font-bold tracking-tight">Criar conta</h2>
            <p className="text-sm text-muted-foreground mt-2">14 dias grátis. Sem cartão.</p>
          </div>

          {/* Plan selection */}
          <div>
            <Label className="mb-2 block">Plano para começar o trial</Label>
            <div className="grid grid-cols-3 gap-2">
              {PLAN_OPTIONS.map((p) => {
                const sel = form.plan === p.id;
                return (
                  <button
                    type="button"
                    key={p.id}
                    data-testid={`plan-pick-${p.id}`}
                    onClick={() => setForm({ ...form, plan: p.id })}
                    className={`relative text-left rounded-lg border p-3 transition-colors ${
                      sel
                        ? "border-primary bg-primary/10"
                        : "border-border hover:border-primary/40"
                    }`}
                  >
                    {p.popular && (
                      <span className="absolute -top-2 right-2 bg-primary text-white text-[9px] uppercase font-semibold px-1.5 py-0.5 rounded">
                        Top
                      </span>
                    )}
                    <div className="font-medium text-sm">{p.name}</div>
                    <div className="text-[10px] text-muted-foreground mt-0.5">
                      R$ {p.price}/mês
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="name">Nome *</Label>
              <Input
                data-testid="signup-name-input"
                id="name"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Seu nome"
                className="mt-2"
              />
            </div>
            <div>
              <Label htmlFor="company">Empresa</Label>
              <Input
                data-testid="signup-company-input"
                id="company"
                value={form.company}
                onChange={(e) => setForm({ ...form, company: e.target.value })}
                placeholder="Nome da loja"
                className="mt-2"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="email">Email *</Label>
            <Input
              data-testid="signup-email-input"
              id="email"
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="voce@empresa.com"
              className="mt-2"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="cpf">CPF / CNPJ *</Label>
              <Input
                data-testid="signup-cpf-input"
                id="cpf"
                required
                value={form.cpf_cnpj}
                onChange={(e) => setForm({ ...form, cpf_cnpj: e.target.value })}
                placeholder="Apenas números"
                className="mt-2"
              />
            </div>
            <div>
              <Label htmlFor="phone">Telefone</Label>
              <Input
                data-testid="signup-phone-input"
                id="phone"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="(11) 99999-9999"
                className="mt-2"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="password">Senha *</Label>
            <Input
              data-testid="signup-password-input"
              id="password"
              type="password"
              required
              minLength={6}
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder="Mínimo 6 caracteres"
              className="mt-2"
            />
          </div>

          <Button
            data-testid="signup-submit-btn"
            type="submit"
            disabled={loading}
            className="w-full h-11 bg-primary hover:bg-primary/90"
          >
            {loading ? "Criando conta e dados demo..." : "Começar trial de 14 dias"}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>

          <p className="text-sm text-muted-foreground text-center">
            Já tem conta?{" "}
            <Link data-testid="goto-login-link" to="/login" className="text-primary hover:underline">
              Entrar
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
